// Import 3rd-party dependencies
import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { interval, of, timer } from 'rxjs';
import { startWith, switchMap } from 'rxjs/operators';
import { SubSink } from 'subsink';
import * as momentTz from 'moment-timezone';

// Import project-specific dependencies
import {
  UserProfileService,
  IClockSettingsDocument,
  IClockSettings,
  ITimeZoneLocale
} from '@app/services/user-profile/user-profile.service';
import { InsightsService, IClickEventData } from '@app/core/analytics/services/app-insights-service';
import { ClockDialogComponent } from '@app/hub/world-clock/clock-dialog/clock-dialog.component';
import { WORLD_CLOCK_COMPONENT_CONSTS } from '@app/config/constants';

interface IClockMenuList {
  favorites: ITimeZoneLocale[];
  recents: ITimeZoneLocale[];
}

interface IClockSettingsStatus {
  status: string;
  reason: string;
}

@Component({
  selector: 'app-world-clock',
  templateUrl: './world-clock.component.html',
  styleUrls: ['./world-clock.component.css']
})
export class WorldClockComponent implements OnInit, OnDestroy {
  private subs: SubSink;

  // Class attributes
  clockSettingsDocument: IClockSettingsDocument;
  menuList: IClockMenuList;
  today: Date;
  menuTimeout: NodeJS.Timeout;
  clockSettingsStatus: IClockSettingsStatus | null;
  isLoading: boolean;
  @ViewChild(MatMenuTrigger) trigger: MatMenuTrigger;

  consts = WORLD_CLOCK_COMPONENT_CONSTS;

  today$ = interval(this.consts.DATE_INTERVAL).pipe(
    startWith(0),
    switchMap(() => of(new Date()))
  );

  // Class methods
  constructor(
    private userProfileService: UserProfileService,
    public clockDialog: MatDialog,
    private trackingService: InsightsService
  ) {}

  ngOnInit(): void {
    this.subs = new SubSink();
    this.clockSettingsDocument = this.consts.DEFAULT_SETTINGS;
    this.isLoading = true;

    this.setMenuList();
    this.setClockSettingsStatus();

    this.subs.sink = this.userProfileService.preferences$.subscribe(
      (res) => {
        if (res && res.worldclock) {
          this.clockSettingsDocument = res;

          // Set default clock settings as needed
          this.clockSettingsDocument.worldclock.dateFormat = !this.clockSettingsDocument.worldclock.dateFormat
            ? this.consts.DEFAULT_DATE_FORMAT
            : this.clockSettingsDocument.worldclock.dateFormat;
          this.clockSettingsDocument.worldclock.timeFormat = !this.clockSettingsDocument.worldclock.timeFormat
            ? this.consts.DEFAULT_TIME_FORMAT
            : this.clockSettingsDocument.worldclock.timeFormat;
          this.setMenuList();

          this.setClockSettingsStatus(this.consts.STATUS_SUCCESS, this.consts.REASON_SUCCESS_RETRIEVED);
          this.isLoading = false;
        }
      },
      () => {
        this.setClockSettingsStatus(this.consts.STATUS_ERROR, this.consts.REASON_ERROR_NOT_RETRIEVED);
        this.isLoading = false;
      }
    );
  }

  ngOnDestroy(): void {
    if (this.menuTimeout) {
      clearTimeout(this.menuTimeout);
    }

    this.subs.unsubscribe();
  }

  setClockSettingsStatus(status?: string, reason?: string): void {
    if (!status || !reason) {
      // Reset status
      this.clockSettingsStatus = null;
    } else {
      // Set status and reason
      this.clockSettingsStatus = {
        status: status,
        reason: reason
      };
      // Reset status after
      this.subs.sink = timer(this.consts.STATUS_TIMEOUT).subscribe(() => (this.clockSettingsStatus = null));
    }
  }

  insights(type: string): void {
    let event = '';
    switch (type) {
      case this.consts.ACTION_CLICK:
        event = this.consts.MESSAGE_CLICK;
        break;
      case this.consts.ACTION_CHANGE:
        event = this.consts.MESSAGE_CHANGE;
        break;
    }
    const data: IClickEventData = {
      location: 'World Clock',
      name: event,
      title: event
    };

    this.trackingService.trackClickEvent(data);
  }

  saveClockSettings(): void {
    this.subs.sink = this.userProfileService.addClockSettings(this.clockSettingsDocument).subscribe(
      () => this.setClockSettingsStatus(this.consts.STATUS_SUCCESS, this.consts.REASON_SUCCESS_SAVED),
      () => this.setClockSettingsStatus(this.consts.STATUS_ERROR, this.consts.REASON_ERROR_NOT_SAVED)
    );
  }

  openClockDialog(): void {
    this.trigger.closeMenu();
    if (!this.isLoading) {
      const dialogRef = this.clockDialog.open(ClockDialogComponent, {
        width: this.consts[650],
        data: this.clockSettingsDocument.worldclock,
        autoFocus: false,
        panelClass: 'clock-dialog-container'
      });
      // App insights
      this.insights(this.consts.ACTION_CLICK);

      dialogRef.afterClosed().subscribe((result: IClockSettings) => {
        if (result) {
          this.clockSettingsDocument.worldclock = result;
          this.setMenuList();

          // Save clock settings
          this.saveClockSettings();

          // App insights
          this.insights(this.consts.ACTION_CHANGE);
        }
      });
    }
  }

  openMenu(): void {
    if (
      this.isLoading ||
      (!this.clockSettingsDocument.worldclock.current &&
        this.menuList.favorites.length <= 0 &&
        this.menuList.recents.length <= 0)
    ) {
      return;
    } else {
      if (this.menuTimeout) {
        clearTimeout(this.menuTimeout);
      }
      this.trigger.openMenu();
    }
  }

  closeMenu(): void {
    if (
      this.isLoading ||
      (!this.clockSettingsDocument.worldclock.current &&
        this.menuList.favorites.length <= 0 &&
        this.menuList.recents.length <= 0)
    ) {
      return;
    } else {
      this.menuTimeout = setTimeout(() => {
        this.trigger.closeMenu();
      }, this.consts.MENU_TIMEOUT);
    }
  }

  setMenuList(): void {
    if (!this.menuList) {
      this.menuList = {
        favorites: [],
        recents: []
      };
    }

    this.menuList.favorites = Array.from(this.clockSettingsDocument.worldclock.favorites);
    // TH-2127-Time-Zone-list-expand, comment out in case display small empty pane when user removes all favorites.
    // this.menuList.recents = this.clockSettingsDocument.worldclock.recent
    //   ? this.clockSettingsDocument.worldclock.recent.filter(
    //       (recent) => this.getLocationIndexInList(this.clockSettingsDocument.worldclock.favorites, recent) < 0
    //     )
    //   : [];
  }

  getLocationIndexInList(list: ITimeZoneLocale[], location: ITimeZoneLocale): number {
    if (!location) {
      return -1;
    }

    return list
      .map((location) => {
        return `${location.city}, ${location.province ? location.province + ', ' : ''}${location.country}`;
      })
      .indexOf(`${location.city}, ${location.province ? location.province + ', ' : ''}${location.country}`);
  }

  changeCurrentClock(index: number): void {
    if (index >= 0 && index < this.menuList.favorites.length) {
      this.clockSettingsDocument.worldclock.current = this.menuList.favorites[index];
    } else if (
      // Set current to favorite
      index >= this.menuList.favorites.length &&
      index < this.menuList.favorites.length + this.menuList.recents.length
    ) {
      // Set current to recent search
      this.clockSettingsDocument.worldclock.current = this.menuList.recents[index - this.menuList.favorites.length];
    } else {
      // Set current to null
      this.clockSettingsDocument.worldclock.current = null;
    }
    this.saveClockSettings();
  }

  getTimezoneOffset(timezone: string): string {
    return timezone ? momentTz.utc().clone().tz(timezone).toString().split(' ').splice(-1)[0] : null;
  }
}
